import React, { useState, useEffect, useCallback, useRef } from 'react';
import { STORAGE_KEYS, PLATFORM_NAME } from '../../helpers/constants';
import { LanguageConstants } from '../../helpers/LanguageConstants';
import channelService from '../../services/channelService';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';
import { useChannelAdmin, invalidateAdminCache } from '../../hooks/useChannelAdmin';

import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  IconButton,
  Select,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { ChannelMember, BulkChannelMemberFailure } from '../../services/channelService';
import userService from '../../services/userService';
import { capitalizeFirstLetter } from '../../helpers/stringUtils';

interface MembersTabProps {
  channelId?: string;
  channelName?: string;
  isChannelArchived?: boolean;
}

type MemberRole = 'User' | 'Admin';

// A user picked in the Add Member dialog, waiting to be sent in the bulk request
interface SelectedUser {
  mail_id: string;
  name: string;
  role: MemberRole;
}

// Matches the backend limit on POST /channels/{id}/members/bulk
const MAX_BULK_MEMBERS = 100;


const MembersTab: React.FC<MembersTabProps> = ({ channelId, channelName = LanguageConstants.topic, isChannelArchived = false }) => {
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMemberForRole, setSelectedMemberForRole] = useState<ChannelMember | null>(null);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Add Member Modal states
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [bulkAdding, setBulkAdding] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  // Per-email failures returned in the bulk response `failed`
  const [bulkFailures, setBulkFailures] = useState<BulkChannelMemberFailure[]>([]);

  // Search states for invite modal
  const [searchResults, setSearchResults] = useState<ChannelMember[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  // Role chosen on a search row before the user is selected, keyed by lowercase email
  const [userRoles, setUserRoles] = useState<{ [email: string]: MemberRole }>({});
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  
  // Remove member states
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ChannelMember | null>(null);
  const [removingMember, setRemovingMember] = useState(false);
  
  // Theme context
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();

  // Check if current user is channel admin
  const { isChannelAdmin } = useChannelAdmin(channelId);

  // Get current logged-in user ID and email
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  useEffect(() => {
    const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        setCurrentUserId(userData.user_id || null);
        setCurrentUserEmail(userData.email_id || null);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Helper function to check if a member is the current user
  const isCurrentUser = (member: ChannelMember): boolean => {
    if (!currentUserId && !currentUserEmail) return false;
    // Check by ID or email (case-insensitive)
    const memberEmailLower = member.email?.toLowerCase() || '';
    const currentEmailLower = currentUserEmail?.toLowerCase() || '';
    return member.id === currentUserId || memberEmailLower === currentEmailLower;
  };

  // Refs for scrolling to error messages
  const emailErrorAlertRef = useRef<HTMLDivElement>(null);
  const addMemberDialogContentRef = useRef<HTMLDivElement>(null);

  // Scroll to top when emailError appears
  useEffect(() => {
    if (emailError && addMemberDialogContentRef.current) {
      // Small delay to ensure the Alert is rendered
      setTimeout(() => {
        if (addMemberDialogContentRef.current) {
          // Scroll to the top of the dialog content
          addMemberDialogContentRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [emailError]);

  // Fetch members from API when component mounts or channelId changes
  useEffect(() => {
    const fetchMembers = async () => {
      if (!channelId) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await channelService.getChannelMembers(channelId);
        setMembers(response.members || []);
      } catch (err) {
        console.error('Error fetching channel members:', err);
        setError('Failed to load channel members. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [channelId]);

  // Search users function for invite modal
  const searchUsersForInvite = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim() || searchTerm.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    try {
      setSearchLoading(true);
      let companyId = '';
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        companyId = userData.company_id;
      }
      
      const response = await channelService.searchUsers(companyId, searchTerm);
      setSearchResults(response.members || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search effect for invite modal
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      searchUsersForInvite(inviteEmail);
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [inviteEmail, searchUsersForInvite]);





   // Filter members based on search term (local DOM search)
   const filteredMembers = members.filter(member => {
     if (!memberSearchTerm.trim()) return true;
     
     const searchTerm = memberSearchTerm.toLowerCase();
     return (
       member.name.toLowerCase().includes(searchTerm) ||
       member.email.toLowerCase().includes(searchTerm) ||
       member.username.toLowerCase().includes(searchTerm) ||
       (member.company_name && member.company_name.toLowerCase().includes(searchTerm)) ||
       member.role.toLowerCase().includes(searchTerm)
     );
   });

  // Handle role menu open
  const handleRoleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, member: ChannelMember) => {
    setRoleMenuAnchor(event.currentTarget);
    setSelectedMemberForRole(member);
  };

  // Handle role menu close
  const handleRoleMenuClose = () => {
    setRoleMenuAnchor(null);
    setSelectedMemberForRole(null);
  };

  // Handle role change
  const handleRoleChange = async (newRole: 'User' | 'Admin') => {
    if (!selectedMemberForRole || !channelId) return;

    // Prevent users from changing their own role
    if (isCurrentUser(selectedMemberForRole)) {
      setError('You cannot change your own role.');
      handleRoleMenuClose();
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setUpdatingRole(selectedMemberForRole.id);
      
      // Update the member's role in the API with the required payload structure
      // Convert role to lowercase for the API call
      const payload = {
        role: newRole,
        permissions: {
          additionalProp1: {}
        }
      };
      
      await channelService.updateChannelMember(channelId, selectedMemberForRole.id, payload);

      // W-2: drop the cached admin status so the next three-dots open re-fetches
      // the updated role instead of serving the stale TTL entry.
      invalidateAdminCache(channelId);

      // Update the local state with the formatted role
      setMembers(prevMembers => 
        prevMembers.map(member => 
          member.id === selectedMemberForRole.id 
            ? { ...member, role: newRole }
            : member
        )
      );
      
      setSuccessMessage(`Role updated to ${newRole} successfully!`);
      handleRoleMenuClose();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating member role:', error);
      // You might want to show an error toast here
    } finally {
      setUpdatingRole(null);
    }
  };

  // Get role icon based on role
  const getRoleIcon = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return <SecurityIcon sx={{ fontSize: 16 }} />;
      case 'user':
      default:
        return <PersonIcon sx={{ fontSize: 16 }} />;
    }
  };



  // Existing channel members are hidden from the invite search results
  const memberEmails = new Set(members.map(member => member.email.toLowerCase()));
  const availableSearchResults = searchResults.filter(user => !memberEmails.has(user.email.toLowerCase()));

  const isUserSelected = (email: string) =>
    selectedUsers.some(user => user.mail_id.toLowerCase() === email.toLowerCase());

  // Failed emails from the last bulk request, used to mark the chips that need fixing
  const failedEmails = new Set(
    bulkFailures.map(failure => failure.mail_id.toLowerCase()).filter(Boolean)
  );

  // Select or unselect a search result for the bulk add
  const handleToggleSelectUser = (user: ChannelMember) => {
    const emailKey = user.email.toLowerCase();
    if (isUserSelected(user.email)) {
      setSelectedUsers(prev => prev.filter(selected => selected.mail_id.toLowerCase() !== emailKey));
      return;
    }
    if (selectedUsers.length >= MAX_BULK_MEMBERS) {
      setEmailError(`You can add at most ${MAX_BULK_MEMBERS} members at a time.`);
      return;
    }
    setEmailError(null);
    setSelectedUsers(prev => [
      ...prev,
      { mail_id: user.email.trim(), name: user.name, role: userRoles[emailKey] || 'User' }
    ]);
  };

  // Role change from a search row or a selected chip; keeps both in sync
  const handleSelectedRoleChange = (email: string, role: MemberRole) => {
    const emailKey = email.toLowerCase();
    setUserRoles(prev => ({ ...prev, [emailKey]: role }));
    setSelectedUsers(prev =>
      prev.map(selected => selected.mail_id.toLowerCase() === emailKey ? { ...selected, role } : selected)
    );
  };

  const handleApplyRoleToAll = (role: MemberRole) => {
    setSelectedUsers(prev => prev.map(selected => ({ ...selected, role })));
    setUserRoles(prev => {
      const next = { ...prev };
      selectedUsers.forEach(selected => { next[selected.mail_id.toLowerCase()] = role; });
      return next;
    });
  };

  const handleRemoveSelected = (email: string) => {
    const emailKey = email.toLowerCase();
    setSelectedUsers(prev => prev.filter(selected => selected.mail_id.toLowerCase() !== emailKey));
  };

  // Add all selected users in one POST /channels/{id}/members/bulk request
  const handleBulkAddMembers = async () => {
    if (!channelId) {
      setEmailError('Channel ID is required');
      return;
    }
    if (selectedUsers.length === 0) return;

    setEmailError(null);
    setBulkFailures([]);

    try {
      setBulkAdding(true);

      const response = await channelService.addChannelMembersBulk(
        channelId,
        selectedUsers.map(user => ({ mail_id: user.mail_id, role: user.role })),
        PLATFORM_NAME
      );

      // Refresh members list
      const membersResponse = await channelService.getChannelMembers(channelId);
      setMembers(membersResponse.members || []);

      const failures = channelService.getBulkFailures(response);
      if (failures.length > 0) {
        // Keep only the failed emails in the dialog so the admin can fix or remove them
        const failed = new Set(failures.map(failure => failure.mail_id.toLowerCase()).filter(Boolean));
        setBulkFailures(failures);
        setSelectedUsers(prev => prev.filter(user => failed.has(user.mail_id.toLowerCase())));
      } else {
        handleCloseModal();
      }

      setSuccessMessage(response.message || 'Members added to channel successfully!');
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error adding members:', error);
      setEmailError(error instanceof Error && error.message ? error.message : 'Failed to add members. Please try again.');
    } finally {
      setBulkAdding(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setAddMemberModalOpen(false);
    setInviteEmail('');
    setEmailError(null);
    setBulkFailures([]);
    setSearchResults([]);
    setUserRoles({});
    setSelectedUsers([]);
    setBulkAdding(false);
  };

  // Handle remove member
  const handleRemoveMember = async () => {
    if (!memberToRemove || !channelId) return;

    // Prevent users from deleting themselves
    if (isCurrentUser(memberToRemove)) {
      setError('You cannot remove yourself from the channel.');
      handleRemoveMemberClose();
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setRemovingMember(true);
      
      await channelService.removeChannelMember(channelId, memberToRemove.id, memberToRemove.email);
      
      // Remove member from local state
      setMembers(prevMembers => 
        prevMembers.filter(member => member.id !== memberToRemove.id)
      );
      
      // Close dialog and show success message
      setRemoveMemberDialogOpen(false);
      setMemberToRemove(null);
      setSuccessMessage(`Member ${memberToRemove.name} removed from channel successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error removing member:', error);
      setError('Failed to remove member. Please try again.');
    } finally {
      setRemovingMember(false);
    }
  };

  // Open remove member dialog
  const handleRemoveMemberOpen = (member: ChannelMember) => {
    setMemberToRemove(member);
    setRemoveMemberDialogOpen(true);
  };

  // Close remove member dialog
  const handleRemoveMemberClose = () => {
    setRemoveMemberDialogOpen(false);
    setMemberToRemove(null);
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Control Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        gap: 2
      }}>
        {/* Left side - Action buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isChannelAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddMemberModalOpen(true)}
              disabled={isChannelArchived}
              sx={{
                backgroundColor: isChannelArchived ? muiTheme.palette.grey[300] : muiTheme.palette.primary.main,
                color: 'white',
                '&:hover': { backgroundColor: isChannelArchived ? muiTheme.palette.grey[300] : muiTheme.palette.primary.dark }
              }}
            >
              Add Member
            </Button>
          )}
        </Box>

                 {/* Right side - View toggle and search */}
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
           
                                             {/* Search bar */}
             <Box sx={{ position: 'relative' }}>
               <TextField
                 placeholder="Search existing members..."
                 value={memberSearchTerm}
                 onChange={(e) => setMemberSearchTerm(e.target.value)}
                 size="small"
                 sx={{
                   width: 280,
                   '& .MuiOutlinedInput-root': {
                     backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                     borderColor: muiTheme.palette.divider,
                     '&:hover': {
                       borderColor: muiTheme.palette.grey[400]
                     },
                     '&.Mui-focused': {
                       borderColor: muiTheme.palette.primary.main
                     }
                   }
                 }}
                 InputProps={{
                   startAdornment: <SearchIcon sx={{ color: muiTheme.palette.text.secondary, mr: 1 }} />,
                   endAdornment: memberSearchTerm && (
                     <Box 
                       component="span" 
                       sx={{ 
                         cursor: 'pointer',
                         color: muiTheme.palette.text.secondary,
                         '&:hover': { color: muiTheme.palette.text.primary }
                       }}
                       onClick={() => setMemberSearchTerm('')}
                     >
                       ✕
                     </Box>
                   )
                 }}
               />
             </Box>
         </Box>
      </Box>

       {/* Members Grid */}
       <Box sx={{ 
         display: 'grid', 
         gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
         gap: 1.5
       }}>
         {filteredMembers.map((member) => (
          <Paper
            key={member.id}
            elevation={1}
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${muiTheme.palette.divider}`,
              backgroundColor: muiTheme.palette.background.paper,
              '&:hover': {
                boxShadow: isDarkMode ? '0 4px 8px rgba(255,255,255,0.1)' : '0 4px 8px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
               {/* Avatar */}
               <Avatar
                 sx={{
                   bgcolor: member.avatarColor,
                   width: 40,
                   height: 40,
                   fontSize: '1.1rem',
                   fontWeight: 'bold',
                   flexShrink: 0
                 }}
               >
                 {member.avatarInitials}
               </Avatar>

               {/* User Details */}
               <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                {/* Name and Username */}
                <Box sx={{ mb: 0.75 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 'bold', 
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%'
                    }}
                  >
                    {capitalizeFirstLetter(member.name)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%'
                    }}
                  >
                    {member.username}
                  </Typography>
                </Box>

                {/* Email */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon sx={{ fontSize: 16, color: muiTheme.palette.text.secondary, flexShrink: 0 }} />
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      minWidth: 0
                    }}
                  >
                    {member.email}
                  </Typography>
                </Box>

                {/* Organization or Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  {member.company_name ? (
                    <>
                      <BusinessIcon sx={{ fontSize: 16, color: muiTheme.palette.text.secondary, flexShrink: 0 }} />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          minWidth: 0
                        }}
                      >
                        {member.company_name}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <PersonIcon sx={{ fontSize: 16, color: muiTheme.palette.warning.main, flexShrink: 0 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: muiTheme.palette.warning.main,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          minWidth: 0
                        }}
                      >
                        Pending Invite
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Role Management and Remove Button */}
                {isChannelAdmin ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Role change button - disabled for current user */}
                    <Button
                       variant="outlined"
                       startIcon={updatingRole === member.id ? <CircularProgress size={16} /> : getRoleIcon(member.role)}
                       endIcon={<KeyboardArrowDownIcon />}
                       size="small"
                       onClick={(e) => handleRoleMenuOpen(e, member)}
                       disabled={updatingRole === member.id || isChannelArchived || isCurrentUser(member)}
                       sx={{
                         borderColor: muiTheme.palette.divider,
                         color: muiTheme.palette.text.primary,
                         backgroundColor: muiTheme.palette.background.paper,
                         maxWidth: '100%',
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                         '&:hover': {
                           borderColor: muiTheme.palette.grey[400],
                           backgroundColor: muiTheme.palette.action.hover
                         },
                         '&:disabled': {
                           opacity: 0.6,
                           cursor: 'not-allowed'
                         }
                       }}
                     >
                       {/* Display formatted role (e.g., "Admin", "User") from member.role */}
                       {updatingRole === member.id ? 'Updating...' : member.role}
                    </Button>
                    
                    {/* Remove Member Icon Button - hidden for current user */}
                    {!isCurrentUser(member) && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveMemberOpen(member)}
                        sx={{
                          color: muiTheme.palette.error.main,
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: muiTheme.palette.error.light + '20',
                            color: muiTheme.palette.error.dark
                          }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                   <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                     {/* Display role name for non-admin users */}
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                       {getRoleIcon(member.role)}
                       <Typography 
                         variant="body2" 
                         color="text.secondary" 
                         sx={{ 
                           fontWeight: 500,
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap',
                           maxWidth: '100%',
                           minWidth: 0
                         }}
                       >
                         {member.role}
                       </Typography>
                     </Box>
                   </Box>
                )}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Role Management Menu */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor)}
        onClose={handleRoleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ 
          zIndex: 9999,
          '& .MuiPaper-root': {
            zIndex: 9999
          }
        }}
        keepMounted
        disablePortal={false}
      >
        {selectedMemberForRole && !isCurrentUser(selectedMemberForRole) && (
          <>
            <MenuItem 
              onClick={() => handleRoleChange('User')}
              sx={{
                backgroundColor: selectedMemberForRole?.role?.toLowerCase() === 'user' 
                  ? muiTheme.palette.primary.main + '14' 
                  : 'transparent',
                '&:hover': {
                  backgroundColor: selectedMemberForRole?.role?.toLowerCase() === 'user' 
                    ? muiTheme.palette.primary.main + '1e' 
                    : muiTheme.palette.action.hover
                }
              }}
            >
              <ListItemIcon>
                <PersonIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText>User</ListItemText>
              {selectedMemberForRole?.role?.toLowerCase() === 'user' && (
                <Box sx={{ color: muiTheme.palette.primary.main, ml: 1 }}>
                  ✓
                </Box>
              )}
            </MenuItem>
            <MenuItem 
              onClick={() => handleRoleChange('Admin')}
              sx={{
                backgroundColor: selectedMemberForRole?.role?.toLowerCase() === 'admin' 
                  ? muiTheme.palette.primary.main + '14' 
                  : 'transparent',
                '&:hover': {
                  backgroundColor: selectedMemberForRole?.role?.toLowerCase() === 'admin' 
                    ? muiTheme.palette.primary.main + '1e' 
                    : muiTheme.palette.action.hover
                }
              }}
            >
              <ListItemIcon>
                <SecurityIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText>Admin</ListItemText>
              {selectedMemberForRole?.role?.toLowerCase() === 'admin' && (
                <Box sx={{ color: muiTheme.palette.primary.main, ml: 1 }}>
                  ✓
                </Box>
              )}
            </MenuItem>
          </>
        )}
        {selectedMemberForRole && isCurrentUser(selectedMemberForRole) && (
          <MenuItem disabled>
            <ListItemText>You cannot change your own role</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Add Member Modal */}
      <Dialog 
        open={addMemberModalOpen} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return; // Prevent closing when clicking outside
          }
          handleCloseModal();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle>
          <span style={{ color: muiTheme.palette.primary.main, fontWeight: 'bold' }}>{channelName}</span> - Invite Member
        </DialogTitle>
        
          <DialogContent 
            ref={addMemberDialogContentRef}
            sx={{ maxHeight: '70vh', overflow: 'auto' }}
          >
           <Box sx={{ pt: 1 }}>
             {/* Error Message */}
             {emailError && (
               <Box ref={emailErrorAlertRef} sx={{ mb: 2 }}>
                 <Alert severity="error">
                   {emailError}
                 </Alert>
               </Box>
             )}

             {/* Per-email failures from the last bulk add */}
             {bulkFailures.length > 0 && (
               <Alert severity="warning" sx={{ mb: 2 }}>
                 <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                   Some members could not be added:
                 </Typography>
                 <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                   {bulkFailures.map((failure, index) => (
                     <li key={index}>
                       <Typography variant="body2">
                         {failure.mail_id ? `${failure.mail_id}: ${failure.reason}` : failure.reason}
                       </Typography>
                     </li>
                   ))}
                 </Box>
               </Alert>
             )}

             {/* Search Input */}
             <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'medium' }}>
               Search Members
             </FormLabel>
                         <TextField
               fullWidth
               placeholder="Type at least 3 characters to search existing users..."
               value={inviteEmail}
               onChange={(e) => {
                 setInviteEmail(e.target.value);
                 if (emailError) setEmailError(null);
               }}
               sx={{ mb: 3 }}
             />

              {/* Search Results Container - Fixed Height */}
              <Box sx={{ 
                 height: 200,
                 mb: 3,
                 position: 'relative'
               }}>
                                 {/* Loading State - Overlay */}
                 {searchLoading && (
                   <Box sx={{ 
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     backgroundColor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50],
                     borderRadius: 1,
                     zIndex: 1,
                     minHeight: '100%'
                   }}>
                     <CircularProgress size={32} sx={{ mb: 2 }} />
                     <Typography variant="body2" color="text.secondary">
                       Searching for members...
                     </Typography>
                   </Box>
                 )}

                {/* Search Results */}
                {availableSearchResults.length > 0 && (
                  <Box sx={{
                    opacity: searchLoading ? 0.3 : 1,
                    transition: 'opacity 0.3s ease-in-out'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 2 }}>
                      Found Users:
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      {availableSearchResults.map((user) => (
                        <Paper
                          key={user.id}
                          elevation={1}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderRadius: 1,
                            border: `1px solid ${muiTheme.palette.divider}`,
                            backgroundColor: muiTheme.palette.background.paper,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              boxShadow: isDarkMode ? '0 2px 8px rgba(255,255,255,0.1)' : '0 2px 8px rgba(0,0,0,0.1)',
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: user.avatarColor,
                                width: 40,
                                height: 40,
                                fontSize: '1rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {user.avatarInitials}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {capitalizeFirstLetter(user.name)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                {/* MUI Select so role dropdown is fully themed in dark mode (closed + open menu) */}
                                <Select
                                  value={userRoles[user.email.toLowerCase()] || 'User'}
                                  onChange={(e) => handleSelectedRoleChange(user.email, e.target.value as MemberRole)}
                                  disabled={bulkAdding}
                                  displayEmpty
                                  sx={{
                                    fontSize: '14px',
                                    backgroundColor: 'background.paper',
                                    color: 'text.primary',
                                    '& .MuiSelect-select': { py: 1 },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                                  }}
                                  MenuProps={{
                                    // Ensure dropdown appears above the Invite Member dialog (zIndex 9999)
                                    sx: { zIndex: 10000 },
                                    PaperProps: {
                                      sx: {
                                        zIndex: 10000,
                                        backgroundColor: 'background.paper',
                                        color: 'text.primary',
                                        mt: 1,
                                        '& .MuiMenuItem-root': { color: 'text.primary' },
                                      },
                                    },
                                    MenuListProps: { sx: { py: 0 } },
                                  }}
                                >
                                  <MenuItem value="User">User</MenuItem>
                                  <MenuItem value="Admin">Admin</MenuItem>
                                </Select>
                              </FormControl>
                              <Button
                                size="small"
                                variant={isUserSelected(user.email) ? 'outlined' : 'contained'}
                                onClick={() => handleToggleSelectUser(user)}
                                disabled={bulkAdding}
                                startIcon={isUserSelected(user.email) ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                                sx={isUserSelected(user.email) ? {
                                  minWidth: 96
                                } : {
                                  minWidth: 96,
                                  backgroundColor: muiTheme.palette.primary.main,
                                  color: 'white',
                                  '&:hover': { backgroundColor: muiTheme.palette.primary.dark },
                                  '&:disabled': {
                                    backgroundColor: muiTheme.palette.primary.main,
                                    opacity: 0.7,
                                    color: 'white'
                                  }
                                }}
                              >
                                {isUserSelected(user.email) ? 'Selected' : 'Select'}
                              </Button>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* No Results Message */}
                {inviteEmail.length >= 3 && availableSearchResults.length === 0 && !searchLoading && (
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: isDarkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[50], 
                    borderRadius: 1,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      {searchResults.length > 0 ? 'All matching users are already members' : 'No members found'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Try searching with different keywords or check if the user exists in your organization
                    </Typography>
                  </Box>
                )}

                {/* Initial State - Show when no search has been performed */}
                {inviteEmail.length < 3 && searchResults.length === 0 && !searchLoading && (
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: isDarkMode ? muiTheme.palette.grey[900] : muiTheme.palette.grey[50], 
                    borderRadius: 1,
                    border: `1px dashed ${muiTheme.palette.divider}`,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <SearchIcon sx={{ fontSize: 48, color: muiTheme.palette.text.disabled, mb: 2 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, color: muiTheme.palette.text.secondary }}>
                      Start typing to search
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Type at least 3 characters to search for existing users
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Selected users to add in one bulk request */}
              {selectedUsers.length > 0 && (
                <Box sx={{ mb: 2, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      Selected ({selectedUsers.length}/{MAX_BULK_MEMBERS})
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <Select
                        value=""
                        displayEmpty
                        disabled={bulkAdding}
                        onChange={(e) => handleApplyRoleToAll(e.target.value as MemberRole)}
                        renderValue={() => 'Apply role to all'}
                        sx={{
                          fontSize: '14px',
                          backgroundColor: 'background.paper',
                          color: 'text.primary',
                          '& .MuiSelect-select': { py: 0.75 },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                        }}
                        MenuProps={{
                          sx: { zIndex: 10000 },
                          PaperProps: { sx: { zIndex: 10000, backgroundColor: 'background.paper', color: 'text.primary' } },
                        }}
                      >
                        <MenuItem value="User">User</MenuItem>
                        <MenuItem value="Admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 220, overflowY: 'auto' }}>
                    {selectedUsers.map((user) => {
                      const hasFailed = failedEmails.has(user.mail_id.toLowerCase());
                      return (
                        <Box
                          key={user.mail_id}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Chip
                            label={`${capitalizeFirstLetter(user.name)} · ${user.mail_id}`}
                            color={hasFailed ? 'warning' : 'default'}
                            variant={hasFailed ? 'filled' : 'outlined'}
                            onDelete={bulkAdding ? undefined : () => handleRemoveSelected(user.mail_id)}
                            sx={{ flex: 1, justifyContent: 'space-between', minWidth: 0 }}
                          />
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={user.role}
                              disabled={bulkAdding}
                              onChange={(e) => handleSelectedRoleChange(user.mail_id, e.target.value as MemberRole)}
                              sx={{
                                fontSize: '13px',
                                backgroundColor: 'background.paper',
                                color: 'text.primary',
                                '& .MuiSelect-select': { py: 0.5 },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                              }}
                              MenuProps={{
                                sx: { zIndex: 10000 },
                                PaperProps: { sx: { zIndex: 10000, backgroundColor: 'background.paper', color: 'text.primary' } },
                              }}
                            >
                              <MenuItem value="User">User</MenuItem>
                              <MenuItem value="Admin">Admin</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

                         
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, borderTop: `1px solid ${muiTheme.palette.divider}` }}>
          <Button
            onClick={handleCloseModal}
            disabled={bulkAdding}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkAddMembers}
            disabled={selectedUsers.length === 0 || bulkAdding}
            startIcon={bulkAdding ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
            sx={{
              backgroundColor: muiTheme.palette.primary.main,
              color: 'white',
              '&:hover': { backgroundColor: muiTheme.palette.primary.dark },
            }}
          >
            {bulkAdding
              ? 'Adding...'
              : `Add ${selectedUsers.length > 0 ? selectedUsers.length + ' ' : ''}Member${selectedUsers.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <Dialog
        open={removeMemberDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return; // Prevent closing when clicking outside
          }
          handleRemoveMemberClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        sx={{ zIndex: 9999 }}
      >
        <DialogTitle sx={{ color: muiTheme.palette.error.main, fontWeight: 'bold' }}>
          Remove Member
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to remove <strong>{memberToRemove?.name}</strong> from this channel?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. The member will lose access to this channel and all its content.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleRemoveMemberClose}
            disabled={removingMember}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveMember}
            disabled={removingMember}
            variant="contained"
            color="error"
            sx={{
              backgroundColor: muiTheme.palette.error.main,
              '&:hover': { backgroundColor: muiTheme.palette.error.dark },
              '&:disabled': { backgroundColor: muiTheme.palette.grey[400] }
            }}
          >
            {removingMember ? 'Removing...' : 'Remove Member'}
          </Button>
        </DialogActions>
      </Dialog>

             {/* Empty state */}
       {filteredMembers.length === 0 && !loading && (
         <Box sx={{ 
           textAlign: 'center', 
           py: 4,
           color: 'text.secondary'
         }}>
           <Typography variant="h6" sx={{ mb: 1 }}>
             {memberSearchTerm 
               ? 'No users found matching your search' 
               : members.length === 0 
                 ? 'No members in this channel' 
                 : 'No members found'
             }
           </Typography>
           <Typography variant="body2">
             {memberSearchTerm 
               ? 'Try adjusting your search terms or search for different keywords.'
               : members.length === 0 
                 ? 'Start by adding members to this channel.' 
                 : 'Try adjusting your search terms or add new members.'
             }
           </Typography>
         </Box>
       )}
    </Box>
  );
};

export default MembersTab; 